import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, PlusCircle, FilterIcon } from 'lucide-react';
import AppliedFilters from '@/components/AppliedFilters';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import DynamicFormModal from '@/components/DynamicFormModal';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { edificiosColumns, columnsExcel } from '@/pages/parametrizacion/edificios/utils/edificiosColumns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { fetchEdificios, createEdificio, updateEdificio, deleteEdificio, exportEdificios } from '@/pages/parametrizacion/edificios/Services/edificios.services';
import { getZonas } from '@/pages/parametrizacion/zonas/Services/zonas.services';
import FilterDrawer from '@/components/FilterDrawer';
import ExportExcelButton from '@/components/ExportExcelButton';
import AssignGarmentTypesModal from '@/components/AssignGarmentTypesModal';
import AssignInsumosModal from '@/components/AssignInsumosModal';

const ITEMS_PER_PAGE = 10;
const DEFAULT_FILTERS = { estado: 'Activo' };

const EdificiosPage = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [displayedData, setDisplayedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [modalMode, setModalMode] = useState('create');

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [zonas, setZonas] = useState([]);

  // Estado para modal de asignación de tipos de prenda
  const [isAssignGarmentTypesModalOpen, setIsAssignGarmentTypesModalOpen] = useState(false);
  const [selectedEdificioForGarmentTypes, setSelectedEdificioForGarmentTypes] = useState(null);

  // Estado para modal de asignación de insumos
  const [isAssignInsumosModalOpen, setIsAssignInsumosModalOpen] = useState(false);
  const [selectedEdificioForInsumos, setSelectedEdificioForInsumos] = useState(null);

  const fetchAndSetEdificios = useCallback(async () => {
    setIsLoading(true);
    try {
      const offset = currentPage;
      const { data, totalRecords } = await fetchEdificios({ 
        limit: ITEMS_PER_PAGE, 
        offset, 
        query: filters,
        userId: user?.id,
        userRole: user?.role
      });
      setDisplayedData(data);
      setTotalItems(totalRecords);
    } catch (error) {
      console.error('Error al obtener edificios:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los edificios. Por favor, intenta de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, filters, user, toast]);

  useEffect(() => {
    fetchAndSetEdificios();
  }, [fetchAndSetEdificios]);

  // Cargar zonas al montar el componente
  useEffect(() => {
    const loadZonas = async () => {
      try {
        const zonasData = await getZonas();
        setZonas(zonasData);
      } catch (error) {
        console.error('Error al cargar zonas:', error);
      }
    };
    loadZonas();
  }, []);

  // Crear columnas dinámicas con opciones de zona
  const dynamicColumns = React.useMemo(() => {
    return edificiosColumns.map(col => {
      if (col.id === 'zona') {
        return {
          ...col,
          options: zonas.map(zona => ({
            value: zona.nombre,
            label: zona.nombre,
          })),
        };
      }
      return col;
    });
  }, [zonas]);

  // Campos de filtro para el FilterDrawer
  const FILTER_FIELDS = React.useMemo(() => [
    {
      id: 'estado',
      label: 'Estado',
      type: 'select',
      options: [
        { value: 'Activo', label: 'Activo' },
        { value: 'Inactivo', label: 'Inactivo' },
      ]
    },
    {
      id: 'nombre',
      label: 'Nombre del Edificio',
      type: 'text',
      placeholder: 'Buscar por nombre...'
    },
    {
      id: 'ciudad',
      label: 'Ciudad',
      type: 'text',
      placeholder: 'Buscar por ciudad...'
    },
    {
      id: 'tipo',
      label: 'Tipo de Edificio',
      type: 'select',
      options: [
        { value: 'casa', label: 'Casa(s)' },
        { value: 'apto', label: 'Apartamento(s)' },
        { value: 'otro', label: 'Otro' },
      ]
    },
    {
      id: 'tipoLogistica',
      label: 'Tipo Logística',
      type: 'select',
      options: [
        { value: 'horizontal', label: 'Horizontal' },
        { value: 'vertical', label: 'Vertical' },
        { value: 'mixto', label: 'Mixto' },
      ]
    },
    {
      id: 'zona',
      label: 'Zona',
      type: 'select',
      options: zonas.map(zona => ({
        value: zona.nombre,
        label: zona.nombre,
      }))
    },
  ], [zonas]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setCurrentPage(1);
  };

  const handleRemoveFilter = (filterKey) => {
    const newFilters = { ...filters };
    delete newFilters[filterKey];
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleCreateEdificio = () => {
    setCurrentItem(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleEditEdificio = (edificio) => {
    setCurrentItem(edificio);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleDeleteEdificio = (edificio) => {
    setItemToDelete(edificio);
    setIsDeleteDialogOpen(true);
  };

  const handleModalSubmit = async (formData) => {
    try {
      if (modalMode === 'create') {
        await createEdificio(formData);
        toast({
          title: "Éxito",
          description: "Edificio creado correctamente.",
        });
      } else {
        await updateEdificio(currentItem._id, formData);
        toast({
          title: "Éxito",
          description: "Edificio actualizado correctamente.",
        });
      }
      setIsModalOpen(false);
      fetchAndSetEdificios();
    } catch (error) {
      console.error('Error al guardar edificio:', error);
      toast({
        title: "Error",
        description: `No se pudo ${modalMode === 'create' ? 'crear' : 'actualizar'} el edificio. Por favor, intenta de nuevo.`,
        variant: "destructive",
      });
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteEdificio(itemToDelete._id);
      toast({
        title: "Éxito",
        description: "Edificio eliminado correctamente.",
      });
      setIsDeleteDialogOpen(false);
      fetchAndSetEdificios();
    } catch (error) {
      console.error('Error al eliminar edificio:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar el edificio. Por favor, intenta de nuevo.",
        variant: "destructive",
      });
    }
  };

  const handleAction = (action, item) => {
    switch (action) {
      case 'assign-garment-types':
        setSelectedEdificioForGarmentTypes(item);
        setIsAssignGarmentTypesModalOpen(true);
        break;
      case 'assign-insumos':
        setSelectedEdificioForInsumos(item);
        setIsAssignInsumosModalOpen(true);
        break;
      case 'edit':
        handleEditEdificio(item);
        break;
      case 'delete':
        handleDeleteEdificio(item);
        break;
      default:
        console.warn('Acción no reconocida:', action);
    }
  };

  // Preparar columnas para exportación
  const exportColumns = [
    { key: 'nombre', header: 'Nombre del Edificio' },
    { key: 'ciudad', header: 'Ciudad' },
    { key: 'direccion', header: 'Dirección' },
    { key: 'numeroHospedajes', header: 'Número de Activos' },
    { key: 'tipo', header: 'Tipo de Edificio' },
    { key: 'estado', header: 'Estado' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className=""
    >
      <Card className="card-gradient-bg shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Building2 className="h-8 w-8 text-foreground" />
              <div>
                <CardTitle className="text-2xl font-bold text-foreground">
                  Gestión de Edificios
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  Administra los edificios
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <ExportExcelButton
                service={exportEdificios}
                filters={filters}
                columns={exportColumns}
                fileName="edificios"
              />
              <Button
                onClick={() => setIsDrawerOpen(true)}
                variant="outline"
                size="sm"
                className="flex items-center space-x-2"
              >
                <FilterIcon className="h-4 w-4" />
                <span>Filtros</span>
              </Button>
              <Button
                onClick={handleCreateEdificio}
                className="flex items-center space-x-2"
              >
                <PlusCircle className="h-4 w-4" />
                <span>Nuevo Edificio</span>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <AppliedFilters
              filters={filters}
              fields={FILTER_FIELDS}
              onRemoveFilter={handleRemoveFilter}
            />
            
            <DataTable
              columns={dynamicColumns}
              data={displayedData}
              isLoading={isLoading}
              onAction={handleAction}
            />
            
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={handlePageChange}
            />
          </div>
        </CardContent>
      </Card>

      <FilterDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        fields={FILTER_FIELDS}
        onApply={(appliedFilters) => {
          setFilters(appliedFilters);
          setCurrentPage(1);
        }}
      />

      <DynamicFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleModalSubmit}
        fields={dynamicColumns.filter(col => !col.actions)}
        item={currentItem}
        mode={modalMode}
        title={modalMode === 'create' ? 'Crear Edificio' : 'Editar Edificio'}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente el edificio
              "{itemToDelete?.nombre}" del sistema.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AssignGarmentTypesModal
        isOpen={isAssignGarmentTypesModalOpen}
        onClose={() => {
          setIsAssignGarmentTypesModalOpen(false);
          setSelectedEdificioForGarmentTypes(null);
        }}
        edificio={selectedEdificioForGarmentTypes}
        onSuccess={() => {
          fetchAndSetEdificios();
        }}
      />

      <AssignInsumosModal
        isOpen={isAssignInsumosModalOpen}
        onClose={() => {
          setIsAssignInsumosModalOpen(false);
          setSelectedEdificioForInsumos(null);
        }}
        edificio={selectedEdificioForInsumos}
        onSuccess={() => {
          fetchAndSetEdificios();
        }}
      />
    </motion.div>
  );
};

export default EdificiosPage;
